import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, switchMap, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getToken();

  // Skip adding token for authentication endpoints (except /me which needs auth) and public endpoints
  const skipAuth = (req.url.includes('/auth/login') || 
                   req.url.includes('/auth/register') ||
                   req.url.includes('/auth/forgot-password') ||
                   req.url.includes('/auth/reset-password') ||
                   req.url.includes('/auth/refresh') ||
                   req.url.includes('/subcategories')) &&
                   !req.url.includes('/auth/me');

  const getCookie = (name: string) => {
    if (typeof document === 'undefined') return undefined;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return undefined;
  };

  const xsrfToken = getCookie('XSRF-TOKEN');
  const headers: any = {};

  if (token && !skipAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (xsrfToken) {
    headers['X-XSRF-Token'] = xsrfToken;
    headers['X-CSRF-Token'] = xsrfToken;
  }

  // Clone with headers and credentials
  const finalReq = req.clone({
    setHeaders: headers,
    withCredentials: true
  });

  return next(finalReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // If we get a 401 and it's not a refresh endpoint, try to refresh the token
      if (error.status === 401 && !req.url.includes('/auth/refresh') && !skipAuth && token) {
        return authService.refreshToken().pipe(
          switchMap((newToken) => {
            const retryReq = req.clone({
              setHeaders: {
                ...headers,
                Authorization: `Bearer ${newToken}`
              },
              withCredentials: true
            });
            return next(retryReq);
          }),
          catchError((refreshError) => {
            authService.logout().subscribe(() => {
              window.location.href = '/login';
            });
            return throwError(() => refreshError);
          })
        );
      }
      return throwError(() => error);
    })
  );
};
